import { jsx as _jsx } from "react/jsx-runtime";
import { setCookie } from "services/cookies-service";
import { CURRENCY_COOKIE_NAME } from "constants/cookies";
import { TIERS, SUPPORTED_CURRENCIES } from "constants/pricing";
import { ENGLISH_LOCALE_ID, FRENCH_LOCALE_ID, GERMAN_LOCALE_ID } from "constants/localization";
import { isInClusterTest } from "./pricing/pricing-page/clusterized-pricing-page-service";
import PriceWithCurrency from "../segments/desktop/core-components/pricing/price-with-currency/price-with-currency";
import { CRM } from "constants/clusters";
import { shouldShowCrmPricingNoBasicTest, shouldHideCrmSeatsSelection } from "./ab-tests/acquisition-ab-test-service";
import { PLATFORM_CRM_PRODUCT_NAME, PLATFORM_WORK_MANAGEMENT_PRODUCT_NAME } from "../constants/products";
import { UNIT_OF_MEASURE } from "segments/desktop/pricing/constants";
export const CURRENCY_SIGN_RIGHT_OF_NUMBER_LOCALES = [
    FRENCH_LOCALE_ID,
    GERMAN_LOCALE_ID
];
export const USD_CURRENCY = "usd";
export const EURO_COUNTRIES = [
    "ad",
    "at",
    "be",
    "cy",
    "ee",
    "fi",
    "fr",
    "de",
    "gr",
    "ie",
    "it",
    "lv",
    "lt",
    "lu",
    "mt",
    "mc",
    "me",
    "nl",
    "pt",
    "sm",
    "sk",
    "si",
    "es",
    // NEW - starting from Jul 2022
    "al",
    "bh",
    "by",
    "ba",
    "bg",
    "hr",
    "cz",
    "dk",
    "eg",
    "fo",
    "ge",
    "gi",
    "hu",
    "is",
    "jo",
    "kw",
    "li",
    "mk",
    "md",
    "ma",
    "no",
    "om",
    "pl",
    "qa",
    "ro",
    "rs",
    "se",
    "ch",
    "sa",
    "tn",
    "tr",
    "ug",
    "ua",
    "ae",
    "va",
    "ye" // Yemen
];
export const GBP_COUNTRIES = [
    "gb",
    "je",
    "gg",
    "im",
    "gs",
    "uk" // United Kingdom
];
export const CAD_COUNTRIES = [
    "ca" // Canada
];
export const AUD_COUNTRIES = [
    "au" // Australia
];
export const PESO_COUNTRIES = [
    "mx" // Mexico
];
export const REAL_COUNTRIES = [
    "br" // Brazil
];
export const JPY_COUNTRIES = [
    "jp" // Japan
];
const CONTACT_US_THRESHOLD = 50;
export const setCurrencyCookie = function(currencyShortText) {
    setCookie(CURRENCY_COOKIE_NAME, currencyShortText);
};
const getCurrencyFromCountry = (country)=>{
    if (country) {
        const countryCode = country.toLowerCase();
        if (EURO_COUNTRIES.indexOf(countryCode) > -1) {
            setCurrencyCookie("eur");
            return "eur";
        }
        if (GBP_COUNTRIES.indexOf(countryCode) > -1) {
            setCurrencyCookie("gbp");
            return "gbp";
        }
        if (CAD_COUNTRIES.indexOf(countryCode) > -1) {
            setCurrencyCookie("cad");
            return "cad";
        }
        if (AUD_COUNTRIES.indexOf(countryCode) > -1) {
            setCurrencyCookie("aud");
            return "aud";
        }
        if (PESO_COUNTRIES.indexOf(countryCode) > -1) {
            setCurrencyCookie("mxn");
            return "mxn";
        }
        if (REAL_COUNTRIES.indexOf(countryCode) > -1) {
            setCurrencyCookie("brl");
            return "brl";
        }
        if (JPY_COUNTRIES.indexOf(countryCode) > -1) {
            setCurrencyCookie("jpy");
            return "jpy";
        }
        //default to usd
        setCurrencyCookie("usd");
        return "usd";
    } else {
        //no country cookie - we return undefined and rely on the default configuration file
        //TODO: log an error? we are server side.
        return undefined;
    }
};
const getCurrency = (forceCurrencyShortText, pricingDefaults, countryCode)=>{
    // first check query param exists.
    if (forceCurrencyShortText) {
        return forceCurrencyShortText;
    }
    if (countryCode) {
        return getCurrencyFromCountry(countryCode);
    }
    return (pricingDefaults === null || pricingDefaults === void 0 ? void 0 : pricingDefaults.defaultCurrency) || USD_CURRENCY;
};
export const getCurrencyData = function(currency) {
    const currenciesArray = SUPPORTED_CURRENCIES.filter(function(currencyItem) {
        //find not supported in ie
        return currencyItem.name === currency;
    });
    return !currenciesArray || currenciesArray.length === 0 ? SUPPORTED_CURRENCIES[0] : currenciesArray[0];
};
export const getCurrencyShortText = function(currency) {
    const currencyData = getCurrencyData(currency);
    if (currencyData.shouldDisplayText) {
        return currencyData.currencyShortText;
    }
    return null;
};
export const getCurrencyName = ({ forceCurrency, pricingDefaults = {}, countryCode })=>{
    const shortCurrencyText = getCurrency(forceCurrency, pricingDefaults, countryCode);
    const currencyArray = SUPPORTED_CURRENCIES.filter(function(supportedCurrency) {
        //find not supported in ie
        return supportedCurrency.currencyShortText.toLowerCase() === shortCurrencyText.toLowerCase();
    });
    return !currencyArray || currencyArray.length === 0 ? SUPPORTED_CURRENCIES[0].name : currencyArray[0].name;
};
export const getCurrencySign = function(currency) {
    const currencyData = getCurrencyData(currency);
    return currencyData.currencySign;
};
export const shouldShowCurrencyText = (currency)=>{
    const lowerCaseCurrency = currency.toLowerCase();
    switch(lowerCaseCurrency){
        case "cad":
        case "aud":
        case "mxn":
            return true;
        default:
            return false;
    }
};
// For some locales, the currency sign (e.g. $) comes to the right of the price value.
export const getCurrencyWithPriceDisplay = ({ price, currency, localeId = ENGLISH_LOCALE_ID, hideCurrency = false, className, unitOfMeasure = null })=>{
    if (hideCurrency) {
        return price;
    }
    const currencySign = getCurrencySign(currency);
    const shouldShowCurrencyToTheRight = CURRENCY_SIGN_RIGHT_OF_NUMBER_LOCALES.includes(localeId);
    if (unitOfMeasure && unitOfMeasure === UNIT_OF_MEASURE.CONTACTS && !hideCurrency && shouldShowCurrencyText(currency)) {
        return shouldShowCurrencyToTheRight ? `${price} ${currencySign}` : `${currencySign}${price}`;
    }
    if (!hideCurrency && shouldShowCurrencyText(currency)) {
        return /*#__PURE__*/ _jsx(PriceWithCurrency, {
            price: price,
            currency: currency,
            currencySign: currencySign,
            className: className
        });
    }
    return shouldShowCurrencyToTheRight ? `${price} ${currencySign}` : `${currencySign}${price}`;
};
const getContactUsThreshold = ({ clusterConfig, abTests, cookies, forcePricing })=>{
    var _clusterConfig_payments_view_pricing, _clusterConfig_payments_view;
    if (!isInClusterTest(clusterConfig, {
        abTests,
        cookies,
        forcePricing
    })) return CONTACT_US_THRESHOLD;
    return (clusterConfig === null || clusterConfig === void 0 ? void 0 : (_clusterConfig_payments_view = clusterConfig.payments_view) === null || _clusterConfig_payments_view === void 0 ? void 0 : (_clusterConfig_payments_view_pricing = _clusterConfig_payments_view.pricing) === null || _clusterConfig_payments_view_pricing === void 0 ? void 0 : _clusterConfig_payments_view_pricing.contact_us_threshold) || CONTACT_US_THRESHOLD;
};
export const shouldSeeContactUs = function(tier, usersCount, contactUsThresholdOptions = {}) {
    return tier === TIERS.ENTERPRISE || usersCount.includes("+") || Number(usersCount) >= getContactUsThreshold(contactUsThresholdOptions);
};
export const shouldSeeNewPricing = (productName)=>{
    return productName === CRM;
};
// Card chrome (title, feature-list rendering, security logos, tier
// description position, learn-more video, horizontal separator) is decoupled
// from the price-display concerns still driven by `shouldSeeNewPricing` and
// `isCrossedPricingUI`. CRM aligns to the work-management (core) chrome via
// this helper while keeping its own price display.
export const shouldUseClassicCardChrome = ({ productName })=>productName === CRM;
export const shouldHideSeatsSelection = ({ productName, abTests })=>{
    if ([
        PLATFORM_CRM_PRODUCT_NAME
    ].includes(productName)) {
        return shouldHideCrmSeatsSelection(abTests, true);
    }
    return false;
};
export const shouldHideBasicTier = ({ persistantCookies, clusterId, productName, abTests, forceSunsetBasic })=>{
    if ([
        PLATFORM_CRM_PRODUCT_NAME
    ].includes(productName) || [
        CRM
    ].includes(clusterId)) {
        return shouldShowCrmPricingNoBasicTest(abTests, true);
    }
    return false;
};
